import { useSelector, useDispatch } from 'react-redux'
import React, { useState, useEffect, useRef } from 'react'
import FormsyForm from 'formsy-react'
import PropTypes from 'prop-types'
import _omit from 'lodash/omit'
import _get from 'lodash/get'
import _parseInt from 'lodash/parseInt'
import { format as formatDateTime, getDateTime } from '../../../services/date-time/index'
import { setOverlayDialogVisible, setCheckoutLoginData } from '../../../services/dialog/actions'
import { selectStaticTextByKey } from '../../../services/static-text/selectors'
import {
  selectEmailAvailabilityProcessing,
  selectHasEmailAvailable,
  selectEmailAvailability,
  selectHasChildProfile,
  selectIsChildProfile,
  selectFirstName,
  selectEmail,
  selectPrimaryLanguage,
  selectLastName,
} from '../../../services/user/selectors'
import { userCreate } from '../../../services/user/index'
import { GiftLoading } from '../GiftLoading/index'
import { H3, HEADING_TYPES } from '../../Heading/index'
import Button, { TYPES } from '../../Button/index'
import { TYPE_LOGIN } from '../../../services/dialog/index'
import { selectLocale } from '../../../services/page/index'
import {
  resetUserEmailAvailability,
  getUserEmailAvailability,
  resetUserChildAccount,
} from '../../../services/user/actions'
import {
  setAuthPersistentData,
  setAuthLoginSuccess,
  setAuthLoginFailed,
} from '../../../services/auth/actions'
import {
  selectGiftOrderProcessing,
  selectRecipientEmail,
  selectDeliveryDate,
  selectGift,
} from '../../../services/gift/selectors'
import {
  GIFT_READY_STATE,
} from '../../../services/gift/index'
import {
  getMaxDeliveryDate,
} from '../../../services/gift/policies/index'
import {
  FORM_INPUT_NAME_PASSWORD,
} from '../../../services/form/index'
import { getPasswordValidation } from '../../../services/password/index'
import {
  EmailInput,
  DateInput,
  TextInput,
  Textarea,
  Password,
} from '../../FormInput.v2/index'
import {
  GIFT_FORM_STATIC_KEY as staticTextStoreKey,
} from './gift-form-static-key'

const GiftFormV2PropTypes = {
  toggleEmailFields: PropTypes.func,
  sendByEmail: PropTypes.bool,
  editing: PropTypes.bool,
  submit: PropTypes.func,
}

const invalidName = 'Friend of Gaia'

function getHeaderText (loggedIn, canPurchaseGift, staticText) {
  if (loggedIn && (canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE)) return staticText.get('cannotPurchaseHeader')
  if (loggedIn && (canPurchaseGift === GIFT_READY_STATE.CAN_PURCHASE)) return staticText.get('yourInfo')
  return staticText.get('yourInfo')
}

export default function GiftFormV2 (props) {
  const staticText = useSelector(selectStaticTextByKey(staticTextStoreKey))
  const orderProcessing = useSelector(selectGiftOrderProcessing)
  const firstName = useSelector(selectFirstName)
  const lastName = useSelector(selectLastName)
  const locale = useSelector(selectLocale)
  const email = useSelector(selectEmail)
  const gift = useSelector(selectGift)
  const language = useSelector(selectPrimaryLanguage)
  const dispatch = useDispatch()

  const {
    toggleEmailFields,
    completePurchase,
    canPurchaseGift,
    toggleEditing,
    sendByEmail,
    reviewing,
    loggedIn,
    editing,
    submit,
  } = props

  const today = formatDateTime(new Date(), locale, 'YYYY-MM-DD')
  const intitialGifterName = firstName !== invalidName ? firstName : ''
  const gifterName = gift.getIn(['details', 'gifterName'], '') !== '' ? gift.getIn(['details', 'gifterName'], '') : intitialGifterName
  const recipientEmail = useSelector(selectRecipientEmail)
  const unformattedDeliveryDate = useSelector((state) => selectDeliveryDate(state, today))
  const emailAvailability = useSelector(selectEmailAvailability)
  const emailAvailabilityProcessing = useSelector(selectEmailAvailabilityProcessing)
  const hasEmailAvailable = useSelector(selectHasEmailAvailable)
  const hasChildProfile = useSelector(selectHasChildProfile)
  const isChildProfile = useSelector(selectIsChildProfile)
  const [recipientEmailValue, setRecipientEmailValue] = useState(recipientEmail || '')
  const [emailValue, setEmailValue] = useState(email || '')
  const [firstNameValue, setFirstNameValue] = useState(firstName || '')
  const [lastNameValue, setLastNameValue] = useState(lastName || '')
  const [recipientFirstNameValue, setRecipientFirstNameValue] = useState(recipientFirstName || '')
  const [recipientLastNameValue, setRecipientLastNameValue] = useState(recipientLastName || '')
  const [formValid, setFormValid] = useState(false)
  const [shouldShowLoginModal, setShouldShowLoginModal] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [userCreateError, setUserCreateError] = useState(false)
  const formRef = useRef(null)
  const deliveryDate = unformattedDeliveryDate
    // getDateTime to standardize incoming format
    ? formatDateTime(getDateTime(unformattedDeliveryDate), locale, 'YYYY-MM-DD')
    : null

  // Update form validity when email availability changes
  useEffect(() => {
    const forcedError = getForcedEmailError()
    if (forcedError) {
      setFormValid(false)
    }
  }, [emailAvailability, hasEmailAvailable, emailValue, shouldShowLoginModal, loggedIn, editing])

  useEffect(() => {
    if (!emailValue && email) {
      setEmailValue(email)
    }
  }, [email])

  // Reset form validation when user logs in or editing state changes
  useEffect(() => {
    // When user logs in or editing state changes, reset form validation
    // and trigger FormsyForm to re-evaluate
    setFormValid(false)
    if (formRef.current) {
      // Force FormsyForm to re-evaluate its validation state
      formRef.current.validateForm()
    }
  }, [loggedIn, editing])

  const showEmailFields = sendByEmail || (recipientEmail && deliveryDate)
  const recipientFirstName = gift.getIn(['details', 'recipientFirstName'], '')
  const recipientLastName = gift.getIn(['details', 'recipientLastName'], '')
  const message = gift.getIn(['details', 'message'], '') || ''
  const showMessage = editing || (!editing && message)
  const showWithOrWithoutEmailBtns = editing && !sendByEmail && !reviewing
  const showNextOrBackBtns = editing && !reviewing && sendByEmail
  const showCompleteOrEditBtns = !editing && reviewing
  const showConfirmOrCancel = editing && reviewing
  const disabled = (!editing && (canPurchaseGift !== GIFT_READY_STATE.CAN_PURCHASE)) || isLoading
  const readonly = !editing && !disabled
  const sectionTopClass = loggedIn
    && (
      canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE
      || canPurchaseGift === GIFT_READY_STATE.CHILD_ACCOUNT
    )
    ? ' gift-form__top--red'
    : ''

  const getForcedEmailError = () => {
    let forcedError = ''

    if (isChildProfile) {
      forcedError = 'isChildAccount'
    } else if (!loggedIn && emailAvailability === false) {
      if (shouldShowLoginModal) {
        dispatch(setCheckoutLoginData(emailValue))
        dispatch(setOverlayDialogVisible(TYPE_LOGIN))
        setShouldShowLoginModal(false)
      }
      forcedError = 'isEmailAvailable'
    } else if (!loggedIn && hasEmailAvailable && (emailAvailability === null)) {
      forcedError = 'isEmailCaptureValid'
    }

    return forcedError
  }

  function handleOnChangeEmail (value) {
    setEmailValue(value)
    if (hasEmailAvailable) {
      dispatch(resetUserEmailAvailability())
    }
    if (hasChildProfile) {
      dispatch(resetUserChildAccount())
    }
    // reset the login modal flag
    if (!shouldShowLoginModal) {
      setShouldShowLoginModal(true)
    }
  }

  const handleOnBlurCheckEmail = (value) => {
    const pattern = /^.+@.+\..{2,}$/
    // We are compensating for Formsy saying the email is valid (when its not)
    // and if the email value equals what has already been checked, then bail
    if (!pattern.test(value)) {
      return
    }
    dispatch(getUserEmailAvailability(value))
  }

  const onValidSubmit = async (data) => {
    if (loggedIn) {
      submit(_omit(data, ['password', 'gifterLastName']))
      return
    }
    setIsLoading(true)
    const userData = await userCreate({
      language: [language],
      email: data.gifterEmail,
      password: data.password,
      firstName: data.gifterName,
      lastName: data.gifterLastName,
      emailOptIn: false,
      inboundTracking: {},
    })

    if (userData.success) {
      const auth = {
        ...(_get(userData, 'auth', {}) || {}),
        uid: _parseInt(_get(userData, 'uid', -1)),
      }
      if (auth.success) {
        dispatch(setAuthPersistentData(auth))
        dispatch(setAuthLoginSuccess(true))
        submit(_omit(data, ['password', 'gifterLastName']))
      } else {
        dispatch(setAuthLoginFailed(userData.codes, userData.statusCode))
      }
    } else {
      setUserCreateError(userData.errorMeta)
    }
    setIsLoading(false)
  }

  const handleLoginClick = () => {
    dispatch(setOverlayDialogVisible(TYPE_LOGIN))
  }

  return (
    <>
      <FormsyForm
        ref={formRef}
        onValid={() => {
          // Only set form as valid if there's no forced email error
          const forcedError = getForcedEmailError()
          setFormValid(!forcedError)
        }}
        onInvalid={() => setFormValid(false)}
        className="gift-form"
        onValidSubmit={onValidSubmit}
      >
        <div className={`gift-form__top ${sectionTopClass}`}>
          <H3
            as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
            className="gift-form__subtitle"
          >
            {getHeaderText(loggedIn, canPurchaseGift, staticText)}
          </H3>
          {!loggedIn && (
            <React.Fragment>
              <div className="gift-form__row">
                <TextInput
                  name="gifterName"
                  validations={{ isSpecialWords: true }}
                  validationErrors={{
                    isSpecialWords: staticText.get('errorFirstName'),
                  }}
                  validationTimeout={750}
                  label={staticText.get('yourFirstName')}
                  maxLength={32}
                  onChange={(value) => setFirstNameValue(value)}
                  value={firstNameValue || firstName}
                  tabIndex="1"
                  required
                  autocomplete="given-name"
                />
                <TextInput
                  validations={{ isSpecialWords: true }}
                  validationErrors={{
                    isSpecialWords: staticText.get('errorLastName'),
                  }}
                  validationTimeout={750}
                  label={staticText.get('yourLastName')}
                  name="gifterLastName"
                  maxLength={32}
                  onChange={(value) => setLastNameValue(value)}
                  value={lastNameValue || lastName}
                  tabIndex="2"
                  required
                  autocomplete="family-name"
                />
              </div>
              <div className="gift-form__row">
                <EmailInput
                  validationErrors={{
                    isEmail: staticText.get('emailValidError'),
                    isEmailAvailable: staticText.get('emailAvailableError'),
                    isEmailCaptureValid: staticText.get('errorMessageInvalidEmail'),
                    isChildAccount: staticText.get('childAccountError'),
                  }}
                  className="gift-form__email"
                  disabled={emailAvailabilityProcessing}
                  validations={{
                    isEmail: true,
                  }}
                  forceError={getForcedEmailError()}
                  label={staticText.get('email')}
                  onBlur={handleOnBlurCheckEmail}
                  onChange={handleOnChangeEmail}
                  name="gifterEmail"
                  validationTimeout={750}
                  value={emailValue}
                  tabIndex="3"
                  required
                />
                <Password
                  className="gift-form__password"
                  validations={getPasswordValidation()}
                  validationErrors={{
                    minLength: staticText.get('passwordError'),
                    matchRegexp: staticText.get('passwordError'),
                    maxLength: staticText.get('passwordMaxLengthMessage'),
                  }}
                  validationTimeout={750}
                  name={FORM_INPUT_NAME_PASSWORD}
                  label={staticText.get('createPassword')}
                  showStrength
                  tabIndex="4"
                  showHideTabIndex="5"
                  required
                  autocomplete="new-password"
                  value=""
                />
              </div>
              <p className="gift-form__text">
                {staticText.get('manageGiftLater')} {staticText.get('alreadyHaveAccount')} <Button onClick={handleLoginClick} type={TYPES.LINK}>{staticText.get('login')}</Button>
              </p>
            </React.Fragment>
          )}
          {!userCreateError && (
            <p className="gift-form__text gift-form__text--error">
              {userCreateError}
            </p>
          )}
          {loggedIn && (canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE) && (
            <p className="gift-form__text">
              {staticText.get('cannotPurchaseBody')}
            </p>
          )}
          {loggedIn && (canPurchaseGift === GIFT_READY_STATE.CHILD_ACCOUNT) && (
            <p className="gift-form__text">
              {staticText.get('childAccountWarning')}
            </p>
          )}
          {loggedIn && (canPurchaseGift === GIFT_READY_STATE.CAN_PURCHASE) && (
            <React.Fragment>
              <div className="gift-form__row">
                <TextInput
                  name="gifterName"
                  label={staticText.get('firstName')}
                  value={gifterName}
                  disabled={disabled}
                  readonly={readonly}
                  required={editing}
                  maxLength={32}
                  tabIndex="1"
                />
                <EmailInput
                  label={staticText.get('email')}
                  disabled={disabled}
                  required={editing}
                  name="gifterEmail"
                  value={emailValue}
                  tabIndex="2"
                  readonly
                />
              </div>
            </React.Fragment>
          )}
        </div>
        <div className="gift-form__lower">
          <H3
            as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
            className="gift-form__subtitle"
          >
            {staticText.get('recipientInfo')}
          </H3>
          <div className="gift-form__row">
            <TextInput
              label={staticText.get('recipientFirstName')}
              value={recipientFirstNameValue || recipientFirstName}
              name="recipientFirstName"
              disabled={disabled}
              readonly={readonly}
              required={editing}
              maxLength={32}
              tabIndex="5"
              onChange={(value) => setRecipientFirstNameValue(value)}
            />
            <TextInput
              label={staticText.get('recipientLastName')}
              value={recipientLastNameValue || recipientLastName}
              name="recipientLastName"
              disabled={disabled}
              readonly={readonly}
              required={editing}
              maxLength={32}
              tabIndex="6"
              onChange={(value) => setRecipientLastNameValue(value)}
            />
          </div>
          <div className="gift-form__row">
            {showEmailFields && reviewing && (
              <>
                <EmailInput
                  label={staticText.get('recipientEmail')}
                  className="gift-form__recipient-email"
                  value={recipientEmailValue}
                  name="recipientEmail"
                  disabled={disabled}
                  readonly={readonly}
                  required={editing}
                  tabIndex="8"
                />
                <DateInput
                  label={staticText.get('deliveryDate')}
                  max={getMaxDeliveryDate(today)}
                  value={deliveryDate || today}
                  readonly={readonly}
                  disabled={disabled}
                  name="deliveryDate"
                  required={editing}
                  min={today}
                  tabIndex="9"
                />
              </>
            )}
          </div>
          {showMessage && (
            <React.Fragment>
              <H3
                as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
                className="gift-form__subtitle"
              >{staticText.get('message')}
              </H3>
              <Textarea
                label={staticText.get('message')}
                validations="maxLength:500"
                showCharCount={editing}
                readonly={readonly}
                disabled={disabled}
                value={message}
                name="message"
                tabIndex="7"
                block
              />
            </React.Fragment>
          )}
          {sendByEmail || reviewing ? null : (
            <p className="gift-form__text">
              {staticText.get('ifYouKnow')}
            </p>
          )}
          {showEmailFields && !reviewing ? (
            <React.Fragment>
              <H3
                as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
                className="gift-form__subtitle"
              >{staticText.get('recipientEmail')}
              </H3>
              <EmailInput
                validationErrors={{
                  isEmail: staticText.get('emailValidError'),
                }}
                className="gift-form__email"
                disabled={disabled}
                validations={{
                  isEmail: true,
                }}
                label={staticText.get('recipientEmail')}
                onChange={(value) => setRecipientEmailValue(value)}
                validationTimeout={750}
                name="recipientEmail"
                readonly={readonly}
                required={editing}
                value={recipientEmailValue}
                tabIndex="8"
              />
              {sendByEmail && (
                <p className="gift-form__date-copy">
                  {staticText.get('whichDay')}
                </p>
              )}
              <DateInput
                label={staticText.get('deliveryDate')}
                max={getMaxDeliveryDate(today)}
                value={deliveryDate || today}
                readonly={readonly}
                disabled={disabled}
                name="deliveryDate"
                required={editing}
                min={today}
                tabIndex="9"
              />
            </React.Fragment>
          ) : null}
          <GiftLoading
            visible={orderProcessing}
            loadingText={staticText.get('pleaseWait')}
          />
          <div className="gift-form__btns">
            {showWithOrWithoutEmailBtns ? (
              <React.Fragment>
                <Button
                  className="gift-form__btn"
                  type={TYPES.SECONDARY}
                  disabled={disabled || !formValid}
                  submit
                >{staticText.get('giveWithoutEmail')}
                </Button>
                <Button
                  onClick={toggleEmailFields}
                  className="gift-form__btn"
                  type={TYPES.PRIMARY}
                  disabled={disabled}
                >{staticText.get('sendByEmail')}
                </Button>
              </React.Fragment>
            ) : null}
            {showNextOrBackBtns ? (
              <React.Fragment>
                <Button
                  onClick={toggleEmailFields}
                  className="gift-form__btn"
                  type={TYPES.SECONDARY}
                  disabled={disabled}
                >{staticText.get('back')}
                </Button>
                <Button
                  disabled={
                    !formValid
                    || disabled
                    || !!getForcedEmailError()
                  }
                  className="gift-form__btn"
                  type={TYPES.PRIMARY}
                  submit
                >{staticText.get('next')}
                </Button>
              </React.Fragment>
            ) : null}
            {showCompleteOrEditBtns ? (
              <React.Fragment>
                <Button
                  disabled={disabled || orderProcessing}
                  className="gift-form__btn"
                  onClick={toggleEditing}
                  type={TYPES.SECONDARY}
                >{staticText.get('edit')}
                </Button>
                <Button
                  disabled={disabled || orderProcessing}
                  onClick={completePurchase}
                  className="gift-form__btn"
                  type={TYPES.PRIMARY}
                >
                  {orderProcessing
                    ? staticText.get('processing', '')
                    : staticText.get('complete')}
                </Button>
              </React.Fragment>
            ) : null}
            {showConfirmOrCancel ? (
              <React.Fragment>
                <Button
                  className="gift-form__btn"
                  type={TYPES.SECONDARY}
                  disabled={disabled}
                  submit
                >{staticText.get('confirm')}
                </Button>
                <Button
                  onClick={toggleEditing}
                  className="gift-form__btn"
                  disabled={disabled}
                  type={TYPES.PRIMARY}
                >{staticText.get('cancel')}
                </Button>
              </React.Fragment>
            ) : null}
          </div>
        </div>
      </FormsyForm>
    </>
  )
}

GiftFormV2.propTypes = GiftFormV2PropTypes
